import { isBareNoPersonaList, isNoPersonaGroup, type OpportunityHubGroup } from "./hub-chrome-grouping.js";
import type { HubChromeOpportunityRow } from "./hub-chrome.types.js";

export type HubOpportunityVirtualItem =
  | { kind: "group"; key: string; groupKey: string; group: OpportunityHubGroup }
  | {
      kind: "row";
      key: string;
      groupKey: string;
      row: HubChromeOpportunityRow;
      indented: boolean;
    }
  | { kind: "cards"; key: string; groupKey: string; rows: HubChromeOpportunityRow[] }
  | { kind: "skeleton"; key: string; groupKey: string | null }
  | {
      kind: "groupFooter";
      key: string;
      groupKey: string;
      shownCount: number;
      totalCount: number;
      hasMore: boolean;
      isLoading: boolean;
    };

export function opportunityGroupCount(group: OpportunityHubGroup): number {
  return group.rollup?.count ?? group.rows.length;
}

/**
 * Flatten groups into the rows a virtualizer mounts. Persona groups keep a
 * header until expanded. A list that is only the no-persona bucket has no header.
 * With `cardsPerLine`, a group's rows are packed into grid lines so every item
 * is exactly one measurable element. With `groupFooters`, a loaded group ends in
 * a footer that pages on demand instead of a skeleton that pages on scroll.
 */
export function buildOpportunityVirtualItems(input: {
  groups: readonly OpportunityHubGroup[];
  expandedGroupKeys: ReadonlySet<string>;
  loadingKeys: ReadonlySet<string>;
  hasMoreByKey: Readonly<Record<string, boolean>>;
  isLoadingMoreGroups: boolean;
  cardsPerLine?: number;
  groupFooters?: boolean;
}): HubOpportunityVirtualItem[] {
  const items: HubOpportunityVirtualItem[] = [];
  const bareNoPersona = isBareNoPersonaList(input.groups);
  for (const group of input.groups) {
    const expanded = bareNoPersona || input.expandedGroupKeys.has(group.key);
    if (!bareNoPersona || !isNoPersonaGroup(group)) {
      items.push({ kind: "group", key: `group:${group.key}`, groupKey: group.key, group });
    }
    if (!expanded) continue;

    if (group.rows.length === 0) {
      items.push({ kind: "skeleton", key: `${group.key}:pending`, groupKey: group.key });
      continue;
    }

    if (input.cardsPerLine) {
      for (let start = 0; start < group.rows.length; start += input.cardsPerLine) {
        const rows = group.rows.slice(start, start + input.cardsPerLine);
        items.push({ kind: "cards", key: `${group.key}:cards:${rows[0]!.id}`, groupKey: group.key, rows });
      }
    } else {
      for (const row of group.rows) {
        items.push({
          kind: "row",
          key: row.id,
          groupKey: group.key,
          row,
          indented: false,
        });
      }
    }
    if (input.groupFooters) {
      items.push({
        kind: "groupFooter",
        key: `${group.key}:footer`,
        groupKey: group.key,
        shownCount: group.rows.length,
        totalCount: opportunityGroupCount(group),
        hasMore: input.hasMoreByKey[group.key] ?? false,
        isLoading: input.loadingKeys.has(group.key),
      });
    } else if (input.loadingKeys.has(group.key) || input.hasMoreByKey[group.key]) {
      items.push({ kind: "skeleton", key: `${group.key}:more`, groupKey: group.key });
    }
  }
  if (input.isLoadingMoreGroups) {
    items.push({ kind: "skeleton", key: "groups:more", groupKey: null });
  }
  return items;
}

/** Groups whose children should load: visible, and either expanded or a single row. */
export function groupKeysToLoad(
  groups: readonly OpportunityHubGroup[],
  visibleGroupKeys: ReadonlySet<string>,
  expandedGroupKeys: ReadonlySet<string>,
): string[] {
  const bareNoPersona = isBareNoPersonaList(groups);
  return groups
    .filter(
      (group) =>
        visibleGroupKeys.has(group.key) &&
        (bareNoPersona || expandedGroupKeys.has(group.key)),
    )
    .map((group) => group.key);
}

/** Expanded groups whose trailing skeleton is on screen and still have a page left. */
export function groupKeysToPage(
  items: readonly HubOpportunityVirtualItem[],
  visibleIndexes: readonly number[],
  hasMoreByKey: Readonly<Record<string, boolean>>,
  loadingKeys: ReadonlySet<string>,
): string[] {
  const keys = new Set<string>();
  for (const index of visibleIndexes) {
    const item = items[index];
    if (!item || item.kind !== "skeleton" || !item.groupKey) continue;
    if (loadingKeys.has(item.groupKey) || !hasMoreByKey[item.groupKey]) continue;
    keys.add(item.groupKey);
  }
  return [...keys];
}

export function visibleGroupKeysFromItems(
  items: readonly HubOpportunityVirtualItem[],
  visibleIndexes: readonly number[],
): string[] {
  const keys = new Set<string>();
  for (const index of visibleIndexes) {
    const item = items[index];
    if (item?.groupKey) keys.add(item.groupKey);
  }
  return [...keys];
}

/** The group index grows only when its last header is actually in the scroll window. */
export function shouldLoadMoreGroups(
  groups: readonly OpportunityHubGroup[],
  visibleGroupKeys: ReadonlySet<string>,
  hasMoreGroups: boolean,
  isLoadingMoreGroups: boolean,
): boolean {
  if (!hasMoreGroups || isLoadingMoreGroups || groups.length === 0) return false;
  return visibleGroupKeys.has(groups[groups.length - 1]!.key);
}
